import { computePosition, flip, offset, Placement } from "@floating-ui/dom";

/**
 * Mainly inspired/adapted from: https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/examples/menu-button-links/
 */
class ButtonMenu extends HTMLElement {
  private buttonNode: HTMLButtonElement;
  private menuNode: HTMLDivElement;
  private menuitemNodes: HTMLAnchorElement[];
  private firstMenuitem: HTMLAnchorElement;
  private lastMenuitem: HTMLAnchorElement;
  private placement: Placement;

  /**
   * Called when the element is connected to the DOM.
   *
   * Sets up event listeners for the button, menu items and the component itself.
   * Also initializes the references to the button, menu and menu items.
   *
   * @returns {void}
   */
  connectedCallback() {
    this.buttonNode = this.querySelector("button") as HTMLButtonElement;
    const controls = this.buttonNode.getAttribute("aria-controls") as string;
    this.menuNode = this.querySelector(`#${controls}`) as HTMLDivElement;
    this.menuitemNodes = [];

    this.placement = (this.getAttribute("data-placement") ||
      "bottom-start") as Placement;

    this.buttonNode.addEventListener(
      "keydown",
      this.onButtonKeydown.bind(this),
    );
    this.buttonNode.addEventListener("click", this.onButtonClick.bind(this));

    const nodes = this.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]');

    for (let i = 0; i < nodes.length; i++) {
      const menuitem = nodes[i];
      this.menuitemNodes.push(menuitem);
      menuitem.tabIndex = -1;

      menuitem.addEventListener("keydown", this.onMenuitemKeydown.bind(this));

      menuitem.addEventListener(
        "mouseover",
        this.onMenuitemMouseover.bind(this),
      );

      if (!this.firstMenuitem) {
        this.firstMenuitem = menuitem;
      }
      this.lastMenuitem = menuitem;
    }

    this.addEventListener("focusin", this.onFocusin.bind(this));
    this.addEventListener("focusout", this.onFocusout.bind(this));

    window.addEventListener(
      "mousedown",
      this.onBackgroundMousedown.bind(this),
      true,
    );
  }

  /**
   * Called when the element is disconnected from the DOM.
   *
   * Removes event listeners set up in `connectedCallback` for the button, menu items,
   * and the component itself to prevent memory leaks.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    this.buttonNode.removeEventListener(
      "keydown",
      this.onButtonKeydown.bind(this),
    );
    this.buttonNode.removeEventListener("click", this.onButtonClick.bind(this));

    this.menuitemNodes.forEach((item) => {
      item.removeEventListener("keydown", this.onMenuitemKeydown.bind(this));
      item.removeEventListener(
        "mouseover",
        this.onMenuitemMouseover.bind(this),
      );
    });

    this.removeEventListener("focusin", this.onFocusin.bind(this));
    this.removeEventListener("focusout", this.onFocusout.bind(this));
  }

  /**
   * Sets focus to the specified menu item.
   *
   * @param {HTMLAnchorElement} newMenuitem The menu item to which focus should be set.
   * @returns {void}
   */
  setFocusToMenuitem(newMenuitem: HTMLAnchorElement) {
    this.menuitemNodes.forEach((item) => {
      if (item === newMenuitem) {
        item.tabIndex = 0;
        newMenuitem.focus();
      } else {
        item.tabIndex = -1;
      }
    });
  }

  /**
   * Sets focus to the first menu item in the menu.
   *
   * This method calls `setFocusToMenuitem` with the first menu item
   * ensuring that keyboard navigation starts from the first item.
   *
   * @returns {void}
   */
  setFocusToFirstMenuitem() {
    this.setFocusToMenuitem(this.firstMenuitem);
  }

  /**
   * Sets focus to the last menu item in the menu.
   *
   * This method calls `setFocusToMenuitem` with the last menu item
   * ensuring that keyboard navigation ends at the last item.
   *
   * @returns {void}
   */
  setFocusToLastMenuitem() {
    this.setFocusToMenuitem(this.lastMenuitem);
  }

  /**
   * Sets focus to the previous menu item relative to the given `currentMenuitem`.
   *
   * If `currentMenuitem` is the first item in the menu, this method sets focus
   * to the last item in the menu. Otherwise, it sets focus to the item before
   * `currentMenuitem` in the menu.
   *
   * @param {HTMLAnchorElement} currentMenuitem The menu item relative to which
   *                                            focus should be set.
   * @returns {HTMLAnchorElement} The menu item that was given focus.
   */
  setFocusToPreviousMenuitem(currentMenuitem: HTMLAnchorElement) {
    let newMenuitem;
    let index;

    if (currentMenuitem === this.firstMenuitem) {
      newMenuitem = this.lastMenuitem;
    } else {
      index = this.menuitemNodes.indexOf(currentMenuitem);
      newMenuitem = this.menuitemNodes[index - 1];
    }

    this.setFocusToMenuitem(newMenuitem);

    return newMenuitem;
  }

  /**
   * Sets focus to the next menu item relative to the given `currentMenuitem`.
   *
   * If `currentMenuitem` is the last item in the menu, this method sets focus
   * to the first item in the menu. Otherwise, it sets focus to the item after
   * `currentMenuitem` in the menu.
   *
   * @param {HTMLAnchorElement} currentMenuitem The menu item relative to which
   *                                            focus should be set.
   * @returns {HTMLAnchorElement} The menu item that was given focus.
   */
  setFocusToNextMenuitem(currentMenuitem: HTMLAnchorElement) {
    let newMenuitem;
    let index;

    if (currentMenuitem === this.lastMenuitem) {
      newMenuitem = this.firstMenuitem;
    } else {
      index = this.menuitemNodes.indexOf(currentMenuitem);
      newMenuitem = this.menuitemNodes[index + 1];
    }
    this.setFocusToMenuitem(newMenuitem);

    return newMenuitem;
  }

  // Popup menu methods

  /**
   * Opens the popup menu associated with the button.
   *
   * @description
   * Sets the `display` CSS property of the menu element to `'block'` and
   * sets the `aria-expanded` attribute of the button element to `'true'`.
   *
   * @returns {void}
   */
  openPopup() {
    this.menuNode.style.display = "block";
    this.buttonNode.setAttribute("aria-expanded", "true");

    computePosition(this.buttonNode, this.menuNode, {
      placement: this.placement,
      middleware: [flip(), offset(3)],
    }).then(({ x, y }) => {
      Object.assign(this.menuNode.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }

  /**
   * Closes the popup menu associated with the button.
   *
   * @description
   * Sets the `display` CSS property of the menu element to `'none'` and
   * sets the `aria-expanded` attribute of the button element to `'false'`.
   * This method only takes effect if the menu is currently open.
   *
   * @returns {void}
   */
  closePopup() {
    if (this.isOpen()) {
      this.buttonNode.setAttribute("aria-expanded", "false");
      this.menuNode.style.display = "none";
    }
  }

  /**
   * Determines whether the popup menu is currently open.
   *
   * @description
   * Checks the value of the `aria-expanded` attribute of the button element
   * to determine whether the popup menu is currently open.
   *
   * @returns {boolean} `true` if the popup menu is open, `false` otherwise.
   */
  isOpen() {
    return this.buttonNode.getAttribute("aria-expanded") === "true";
  }

  // Menu event handlers

  /**
   * Event handler for the `focusin` event.
   *
   * @description
   * Adds the `focus` class to the element when it receives focus.
   *
   * @returns {void}
   */
  onFocusin() {
    this.classList.add("focus");
  }

  /**
   * Event handler for the `focusout` event.
   *
   * @description
   * Removes the `focus` class from the element when it loses focus.
   *
   * @returns {void}
   */
  onFocusout() {
    this.classList.remove("focus");
  }

  /**
   * Handles keydown events on the button element.
   *
   * @description
   * This event handler listens for specific key presses when the button is focused:
   * - Opens the popup menu and focuses the first menu item on 'Enter', 'Space', 'ArrowDown' or 'Down' keys.
   * - Closes the popup menu and refocuses the button on 'Escape' or 'Esc' keys.
   * - Opens the popup menu and focuses the last menu item on 'ArrowUp' or 'Up' keys.
   * If any of these actions are triggered, the default action and event propagation are prevented.
   *
   * @param {KeyboardEvent} event - The keyboard event that triggers this handler.
   *
   * @returns {void}
   */
  onButtonKeydown(event: KeyboardEvent) {
    const { key } = event;
    let flag = false;

    switch (key) {
      case " ":
      case "Enter":
      case "ArrowDown":
      case "Down":
        this.openPopup();
        this.setFocusToFirstMenuitem();
        flag = true;
        break;

      case "Esc":
      case "Escape":
        this.closePopup();
        this.buttonNode.focus();
        flag = true;
        break;

      case "Up":
      case "ArrowUp":
        this.openPopup();
        this.setFocusToLastMenuitem();
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /**
   * Handles click events on the button element.
   *
   * @description
   * Toggles the visibility of the popup menu when the button is clicked.
   * If the popup menu is open, it closes the menu and sets focus back to the button.
   * If the popup menu is closed, it opens the menu and sets focus to the first menu item.
   * Prevents the default action and stops event propagation.
   *
   * @param {MouseEvent} event - The mouse event that triggers this handler.
   *
   * @returns {void}
   */
  onButtonClick(event: MouseEvent) {
    if (this.isOpen()) {
      this.closePopup();
      this.buttonNode.focus();
    } else {
      this.openPopup();
      this.setFocusToFirstMenuitem();
    }

    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Handles keydown events on menu item elements.
   *
   * @description
   * Catches keydown events on menu item elements and performs the following actions:
   * - If the Escape key is pressed, closes the popup menu and sets focus to the button element.
   * - If the Up arrow key is pressed, sets focus to the previous menu item.
   * - If the Down arrow key is pressed, sets focus to the next menu item.
   * - If the Home key is pressed, sets focus to the first menu item.
   * - If the End key is pressed, sets focus to the last menu item.
   * - If the Tab key is pressed, closes the popup menu.
   * - If the Space key is pressed, navigates to the href of the menu item.
   * - If any other key is pressed, does nothing.
   * Prevents the default action and stops event propagation.
   *
   * @param {KeyboardEvent} event - The keydown event that triggers this handler.
   *
   * @returns {void}
   */
  onMenuitemKeydown(event: KeyboardEvent) {
    const tgt = event.currentTarget as HTMLAnchorElement;
    const { key } = event;
    let flag = false;

    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    if (event.shiftKey && event.key === "Tab") {
      this.buttonNode.focus();
      this.closePopup();
      flag = true;
    } else {
      switch (key) {
        case "Esc":
        case "Escape":
          this.closePopup();
          this.buttonNode.focus();
          flag = true;
          break;

        case "Up":
        case "ArrowUp":
          this.setFocusToPreviousMenuitem(tgt);
          flag = true;
          break;

        case "ArrowDown":
        case "Down":
          this.setFocusToNextMenuitem(tgt);
          flag = true;
          break;

        case "Home":
        case "PageUp":
          this.setFocusToFirstMenuitem();
          flag = true;
          break;

        case "End":
        case "PageDown":
          this.setFocusToLastMenuitem();
          flag = true;
          break;

        case "Tab":
          this.closePopup();
          break;

        case " ":
        default:
          window.location.href = tgt.href;
          break;
      }
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /**
   * Handles mouseover events on menu item elements.
   *
   * @description
   * This event handler sets focus to the menu item element that triggered the
   * mouseover event. It ensures that the menu item gains focus when hovered over.
   *
   * @param {MouseEvent} event - The mouse event that triggers this handler.
   *
   * @returns {void}
   */
  onMenuitemMouseover(event: MouseEvent) {
    const tgt = event.currentTarget as HTMLElement;
    tgt?.focus();
  }

  /**
   * Handles mousedown events on the document when the popup menu is open.
   *
   * @description
   * This event handler listens for mousedown events on the document when the
   * popup menu is open. If the target of the event is outside the component,
   * the popup menu is closed and focus is returned to the button.
   *
   * @param {MouseEvent} event - The mousedown event that triggers this handler.
   *
   * @returns {void}
   */
  onBackgroundMousedown(event: MouseEvent) {
    if (event.target instanceof HTMLElement && !this.contains(event.target)) {
      if (this.isOpen()) {
        this.closePopup();
        this.buttonNode.focus();
      }
    }
  }
}

window.customElements.define("imos-button-menu", ButtonMenu);
